import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { mvs } from 'react-native-size-matters';

import { useAppSelector } from '../../app/hooks';
import { COLORS } from '../../constants';
import { selectDivRadius } from '../../features/appconfig/store/selectors';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import { useButtonColors } from '../../hooks/useButtonColors';
import i18n from '../../localization/i18n';
import { CustomIcon } from '../CustomIcon';
import CustomText from '../CustomText';

export interface SongHistoryEmptyStateProps {
  isError?: boolean;
  isNotAvailable?: boolean;
  onRetry?: () => void;
}

export const SongHistoryEmptyState: React.FC<SongHistoryEmptyStateProps> = ({
  isError = false,
  isNotAvailable = false,
  onRetry,
}) => {
  const emptyTextFontSize = useAdjustedFontSize(mvs(16));
  const buttonTextFontSize = useAdjustedFontSize(mvs(14));
  const divRadius = useAppSelector(selectDivRadius);
  const { gradientBackgroundColours, iconOrTextColor } = useButtonColors();

  const message = isNotAvailable
    ? i18n.t('songHistory.notAvailableTitle')
    : isError
      ? i18n.t('songHistory.errorTitle')
      : i18n.t('songHistory.emptyTitle');

  return (
    <View style={styles.container}>
      <CustomText
        style={[
          styles.emptyText,
          {
            fontSize: emptyTextFontSize,
            color: COLORS.WHITE,
          },
        ]}
      >
        {message}
      </CustomText>

      {isError && onRetry && (
        <TouchableOpacity onPress={onRetry} activeOpacity={0.7}>
          <LinearGradient
            // gradient initilalized with a single color.
            // prepared for future addition of a secondary color.
            colors={gradientBackgroundColours}
            style={[styles.retryButton, { borderRadius: divRadius }]}
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 0.5 }}
          >
            <CustomIcon
              name="replay"
              size={16}
              style={styles.retryIcon}
              color={iconOrTextColor}
            />
            <CustomText
              style={[
                styles.retryText,
                {
                  fontSize: buttonTextFontSize,
                  color: iconOrTextColor,
                },
              ]}
            >
              {i18n.t('songHistory.retryButton')}
            </CustomText>
          </LinearGradient>
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: mvs(32),
  },
  emptyText: {
    textAlign: 'center',
    opacity: 0.7,
    marginBottom: mvs(20),
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: mvs(10),
    paddingHorizontal: mvs(16),
  },
  retryIcon: {
    marginRight: mvs(8),
  },
  retryText: {},
});
